"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useRouter } from "next/navigation";
import {
  ChevronDown,
  ImageIcon,
  Library,
  Maximize2,
  Mic,
  Minimize2,
  Paperclip,
  Plus,
  Video,
  X,
} from "lucide-react";
import va from "@vercel/analytics";
import { StudioTag } from "@prisma/client";
import type { Editor } from "@tiptap/react";

import { Dialog, DialogContent, DialogTitle } from "ui";
import SelectVideoModal, {
  getDisplayTitle,
} from "@/components/modal/admin/select-video";

import { SimpleEditor } from "@/components/tiptap-templates/simple/simple-editor";
import {
  BlockInsertMenu,
  applyBlockInsertAction,
  type BlockInsertAction,
} from "@/components/tiptap-ui/block-insert-menu";
import { toast } from "sonner";

import { trpc } from "@/lib/trpc/client";
import { trpcBrowserClient } from "@/lib/trpc/browser";
import { FullPost } from "@/lib/types";
import { useMentionSuggestion } from "@/hooks/use-mention-suggestion";
import { uploadImage, validateImage } from "@/lib/image-upload";
import { uploadVideoToMux } from "@/lib/upload-video-to-mux";
import VoiceMessageRecorder from "@/components/voice-message-recorder";
import VideoMessageRecorder from "@/components/video-message-recorder";
import { useStudio } from "@/lib/state/studio";
import { useMember } from "@/lib/state/member";
import EmojiPickerButton from "@/components/emoji-picker-button";
import { LessonMediaUploadCard } from "@/features/posts/components/lesson-media-upload-card";
import AiDescriptionCard from "@/features/posts/components/ai-description-card";
import LoadingDots from "@/components/icons/loading-dots";
import { SchedulePickerModal } from "@/components/posts/schedule-picker-modal";
import PublishMenu, {
  type PublishActionId,
} from "@/features/posts/components/publish-menu";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type LessonMediaResult =
  | { type: "video"; videoId: string; duration: number }
  | { type: "audio"; url: string; duration: number; mimeType: string };

async function getMediaDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = file.type.startsWith("audio/")
      ? document.createElement("audio")
      : document.createElement("video");
    el.preload = "metadata";
    el.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Math.round(el.duration * 1000));
    };
    el.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
    el.src = url;
  });
}

async function uploadLessonMediaFile(
  file: File,
  studioId: string,
): Promise<LessonMediaResult | null> {
  try {
    const isAudio = file.type.startsWith("audio/");
    const mimeType = file.type || (isAudio ? "audio/mp4" : "video/mp4");

    if (isAudio) {
      // Audio stays on the raw-R2 path (out of scope, TRA-2435 follow-up is
      // video-only — Mux is a video service).
      const { presignedUrl, publicUrl } =
        await trpcBrowserClient.uploads.audioUploadUrl.mutate({ mimeType });

      const putRes = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": mimeType },
      });
      if (!putRes.ok) return null;

      const duration = await getMediaDuration(file);
      return { type: "audio", url: publicUrl, duration, mimeType };
    }

    const duration = await getMediaDuration(file);
    const { videoId } = await uploadVideoToMux(file, {
      filename: file.name,
      mimeType,
    });
    return { type: "video", videoId, duration };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_TOPICS = 5;
// Mirrors the cap in truman studio-trpc posts router (create/update).
const MAX_TITLE_LENGTH = 120;
const ANALYTICS_EVENT = "Created Post From Modal";
const ICON_CLASSES =
  "rounded p-1.5 text-grunge40 transition-colors hover:text-grunge60 dark:text-grunge60 dark:hover:text-grunge40";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Visual/textual variant of the modal. The behavior is identical — only
 *  the header title and primary submit button copy change. Used by the
 *  course curriculum view so admins see "Create Lesson" / "Add lesson"
 *  instead of "Create Post" / "Publish". */
export type CreatePostModalMode = "post" | "lesson";

const MODE_COPY: Record<
  CreatePostModalMode,
  {
    createTitle: string;
    editTitle: string;
    submitCreate: string;
    submitEdit: string;
    titlePlaceholder: string;
    bodyPlaceholder: string;
    /** When false, the channel selector ("Posting in: …") is hidden — the
     *  channel is implied by context (e.g. lessons live in the COURSE
     *  channel from which the modal was opened). */
    showChannelSelector: boolean;
  }
> = {
  post: {
    createTitle: "Create Post",
    editTitle: "Edit Post",
    submitCreate: "Publish",
    submitEdit: "Update",
    titlePlaceholder: "Post title",
    bodyPlaceholder: "Write your post",
    showChannelSelector: true,
  },
  lesson: {
    createTitle: "Create Lesson",
    editTitle: "Edit Lesson",
    submitCreate: "Add lesson",
    submitEdit: "Update lesson",
    titlePlaceholder: "Lesson title",
    bodyPlaceholder: "Lesson description",
    showChannelSelector: false,
  },
};

interface CreatePostModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channels: { id: string; name: string; emoji?: string | null }[];
  defaultChannelId?: string;
  /** Switches the header title and submit-button copy. Defaults to "post". */
  mode?: CreatePostModalMode;
  /**
   * A block-insert action queued from a parent (e.g. compact composer that
   * picked an item before the modal mounted). Will be applied to the editor
   * once it becomes ready, then `onPendingBlockActionConsumed` is called so
   * the parent can clear its state.
   */
  pendingBlockAction?: BlockInsertAction | null;
  onPendingBlockActionConsumed?: () => void;
  /**
   * When provided, the modal opens in edit mode — pre-filling the form with
   * the existing post data and calling `posts.update` instead of `posts.create`.
   */
  editPost?: FullPost | null;
  /**
   * Pre-fills the `courseSectionId` of the new lesson — required for posts in
   * COURSE-type channels. Only applied in create mode; in edit mode the
   * section comes from `editPost.courseSectionId`.
   */
  defaultCourseSectionId?: string | null;
  /**
   * Optional callback invoked after a successful create/update. Use this to
   * trigger a tRPC invalidation that `router.refresh()` won't catch (e.g. the
   * curriculum view's `courseSections.list` query).
   */
  onSaved?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function filterTagsByInput(
  allTags: StudioTag[],
  selected: StudioTag[],
  input: string,
): StudioTag[] {
  const selectedIds = new Set(selected.map((t) => t.id));
  const lower = input.toLowerCase();
  return allTags.filter(
    (t) => !selectedIds.has(t.id) && t.name.toLowerCase().includes(lower),
  );
}

function hasExactMatch(tags: StudioTag[], input: string): boolean {
  const lower = input.toLowerCase();
  return tags.some((t) => t.name.toLowerCase() === lower);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CreatePostModal({
  open,
  onOpenChange,
  channels,
  defaultChannelId,
  pendingBlockAction,
  onPendingBlockActionConsumed,
  editPost,
  defaultCourseSectionId,
  onSaved,
  mode = "post",
}: CreatePostModalProps): React.ReactElement {
  const copy = MODE_COPY[mode];
  const router = useRouter();
  const { studio } = useStudio();
  const { currentMember, permissions } = useMember();
  const utils = trpc.useUtils();
  const createTagMutation = trpc.tags.create.useMutation({
    onSuccess: () => {
      void utils.tags.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create tag");
    },
  });
  const mentionSuggestion = useMentionSuggestion();
  const coverInputRef = useRef<HTMLInputElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);

  const isEditMode = Boolean(editPost);

  // ---- State ----
  const [title, setTitle] = useState("");
  const [editorContent, setEditorContent] = useState("");
  const [editor, setEditor] = useState<Editor | null>(null);

  const handleEmojiInsert = useCallback(
    (native: string) => {
      if (!editor) return;
      editor.chain().focus().insertContent(native).run();
    },
    [editor],
  );

  // Apply a queued block-insert action (from compact composer) once the
  // editor instance becomes available.
  useEffect(() => {
    if (!editor || !pendingBlockAction) return;
    applyBlockInsertAction(pendingBlockAction, editor);
    onPendingBlockActionConsumed?.();
  }, [editor, pendingBlockAction, onPendingBlockActionConsumed]);

  const handleBlockSelect = useCallback(
    (action: BlockInsertAction) => {
      if (!editor) return;
      applyBlockInsertAction(action, editor);
    },
    [editor],
  );
  const [selectedChannelId, setSelectedChannelId] = useState(
    defaultChannelId || "",
  );
  const [tags, setTags] = useState<StudioTag[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [allTags, setAllTags] = useState<StudioTag[]>([]);
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [isTagPending, startTagTransition] = useTransition();

  const createPostMutation = trpc.posts.create.useMutation();
  const updatePostMutation = trpc.posts.update.useMutation();
  const setTagsMutation = trpc.posts.setTags.useMutation();
  const [isTagInputFocused, setIsTagInputFocused] = useState(false);
  const [showChannelDropdown, setShowChannelDropdown] = useState(false);
  // TRA-1443: owners/admins publish as the Studio brand by default; the
  // "Posting as" selector lets them opt back to their personal profile.
  // Create mode only — existing posts change author via the editor's picker.
  const [postAsStudio, setPostAsStudio] = useState(false);
  const [showAuthorDropdown, setShowAuthorDropdown] = useState(false);
  const canPostAsStudio = permissions.isOwnerOrAdmin && !isEditMode;
  const [isAudioMode, setIsAudioMode] = useState(false);
  const [pendingAudio, setPendingAudio] = useState<{
    url: string;
    duration: number;
    mimeType: string;
  } | null>(null);
  const [isVideoMode, setIsVideoMode] = useState(false);

  const [pendingVideo, setPendingVideo] = useState<{
    url: string;
    duration: number;
  } | null>(null);
  const [lessonMediaFile, setLessonMediaFile] = useState<File | null>(null);
  const [pendingVideoId, setPendingVideoId] = useState<string | null>(null);
  const [pendingVideoTitle, setPendingVideoTitle] = useState<string | null>(
    null,
  );
  const [showVideoLibrary, setShowVideoLibrary] = useState(false);
  const [isImportingVideo, setIsImportingVideo] = useState(false);

  // ---- Derived ----
  const isTitleTooLong = title.trim().length > MAX_TITLE_LENGTH;
  const isSubmitDisabled =
    !title.trim() ||
    isTitleTooLong ||
    !selectedChannelId ||
    isPending ||
    isTagPending ||
    isTagInputFocused ||
    (isVideoMode && !pendingVideo) ||
    (isAudioMode && !pendingAudio);

  const filteredTags = useMemo(
    () => filterTagsByInput(allTags, tags, tagInput),
    [allTags, tags, tagInput],
  );

  const showTagDropdown =
    tagInput.length > 0 &&
    (filteredTags.length > 0 || !hasExactMatch(allTags, tagInput));

  const selectedChannel = useMemo(
    () => channels.find((ch) => ch.id === selectedChannelId),
    [channels, selectedChannelId],
  );

  // ---- Effects ----

  // Pre-fill form when opening in edit mode
  useEffect(() => {
    if (!open || !editPost) return;
    setTitle(editPost.title ?? "");
    setEditorContent(editPost.content ?? "");
    setCoverImage(editPost.image ?? null);
    if (editPost.channelId) setSelectedChannelId(editPost.channelId);
    if (editPost.tags && editPost.tags.length > 0) setTags(editPost.tags);
    if (editPost.audioUrl) {
      setPendingAudio({
        url: editPost.audioUrl,
        duration: editPost.audioDuration ?? 0,
        mimeType: editPost.audioMimeType ?? "audio/webm",
      });
    }
    if (editPost.videoUrl) {
      setPendingVideo({
        url: editPost.videoUrl,
        duration: editPost.videoDuration ?? 0,
      });
    }
    if (editPost.videoId) {
      setPendingVideoId(editPost.videoId);
      setPendingVideoTitle(null);
    }
    // Set editor content once the editor is ready
  }, [open, editPost]);

  // When in edit mode and editor becomes ready, set its content
  useEffect(() => {
    if (!editor || !editPost?.content || !open) return;
    editor.commands.setContent(editPost.content);
  }, [editor, editPost, open]);

  useEffect(() => {
    if (!open || !studio?.id) return;

    utils.client.tags.list
      .query()
      .then((data) => {
        if (Array.isArray(data)) {
          setAllTags(data as unknown as StudioTag[]);
        }
      })
      .catch(() => {
        // Silently handle — tags are non-critical
      });
  }, [open, studio?.id, utils]);

  // Sync default channel when it changes or modal opens
  useEffect(() => {
    if (defaultChannelId) {
      setSelectedChannelId(defaultChannelId);
    }
  }, [defaultChannelId]);

  // Reset the author selector to its role-based default on every open so a
  // previous session's choice never leaks into a new post.
  useEffect(() => {
    if (!open) return;
    setPostAsStudio(permissions.isOwnerOrAdmin && !isEditMode);
    setShowAuthorDropdown(false);
  }, [open, permissions.isOwnerOrAdmin, isEditMode]);

  // ---- Callbacks ----

  const handleEditorUpdate = useCallback(
    (payload: { markdown?: string; text: string }) => {
      setEditorContent(payload.markdown ?? payload.text);
    },
    [],
  );

  const handleCoverSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;

      const validation = validateImage(file);
      if (!validation.valid) {
        toast.error(validation.error || "Invalid image file");
        return;
      }

      setCoverFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setCoverImage(ev.target?.result as string);
      };
      reader.readAsDataURL(file);
    },
    [],
  );

  const removeCover = useCallback(() => {
    setCoverFile(null);
    setCoverImage(null);
  }, []);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleAddTag = useCallback(
    (tag: StudioTag) => {
      if (tags.length >= MAX_TOPICS) return;
      setTags((prev) => [...prev, tag]);
      setTagInput("");
    },
    [tags.length],
  );

  const handleRemoveTag = useCallback((tagId: string) => {
    setTags((prev) => prev.filter((t) => t.id !== tagId));
  }, []);

  const handleCreateTag = useCallback(() => {
    if (!studio?.id || tags.length >= MAX_TOPICS || !tagInput.trim()) return;

    const name = tagInput.trim();
    const optimisticId = `optimistic-${Date.now()}`;
    const optimisticTag = {
      id: optimisticId,
      name,
      studioId: studio.id,
      createdAt: new Date(),
      updatedAt: new Date(),
      isHidden: false,
      order: null,
    } satisfies StudioTag;

    // Add optimistically and close the dropdown immediately
    setTags((prev) => [...prev, optimisticTag]);
    setTagInput("");

    startTagTransition(async () => {
      try {
        const newTag = await createTagMutation.mutateAsync({ name });
        // Cast: dates arrive as strings over the wire (no superjson) but
        // are not displayed here, so the StudioTag cast is safe.
        setAllTags((prev) => [...prev, newTag as unknown as StudioTag]);
        setTags((prev) =>
          prev.map((t) =>
            t.id === optimisticId ? (newTag as unknown as StudioTag) : t,
          ),
        );
      } catch {
        // Revert on failure
        setTags((prev) => prev.filter((t) => t.id !== optimisticId));
      }
    });
  }, [studio?.id, tags.length, tagInput, createTagMutation]);

  // TRA-1551: adopt the AI draft into the body. Replaces an empty body
  // (emitting the update so editorContent state stays in sync); appends when
  // the user already wrote something. tiptap-markdown parses the markdown.
  const handleInsertAiDraft = useCallback(
    (markdown: string) => {
      if (!editor) return;
      if (editorContent.trim().length === 0) {
        editor.commands.setContent(markdown, { emitUpdate: true });
      } else {
        editor.commands.insertContentAt(
          editor.state.doc.content.size,
          `\n\n${markdown}`,
        );
      }
      editor.commands.focus("end");
    },
    [editor, editorContent],
  );

  const handleSelectChannel = useCallback((channelId: string) => {
    setSelectedChannelId(channelId);
    setShowChannelDropdown(false);
  }, []);

  const resetForm = useCallback(() => {
    setTitle("");
    setEditorContent("");
    setTags([]);
    setTagInput("");
    setCoverFile(null);
    setCoverImage(null);
    setIsExpanded(false);
    setPendingAudio(null);
    setIsAudioMode(false);
    setPendingVideo(null);
    setIsVideoMode(false);
    setLessonMediaFile(null);
    setPendingVideoId(null);
    setPendingVideoTitle(null);
    setShowVideoLibrary(false);
    setIsImportingVideo(false);
  }, []);

  /**
   * Single submit path that builds the payload and dispatches either
   * posts.create or posts.update. The three entry points (publish now /
   * schedule / save as draft) just differ in which fields they set.
   */
  const submitPost = useCallback(
    (
      options:
        | { mode: "publish" }
        | { mode: "schedule"; scheduledAt: Date }
        | { mode: "draft" },
    ) => {
      if (
        !title.trim() ||
        isTitleTooLong ||
        !selectedChannelId ||
        !studio?.id ||
        isPending ||
        isTagPending
      ) {
        return;
      }

      startTransition(async () => {
        // Upload cover image if a new file was selected
        let imageUrl: string | undefined;
        if (coverFile && studio.id) {
          try {
            imageUrl = await uploadImage({
              file: coverFile,
              studioId: studio.id,
            });
          } catch {
            toast.error("Failed to upload cover image");
            return;
          }
        }

        // Upload lesson media file (Browse Files / drag-drop) if provided.
        // Takes precedence over the recorded video only when no recorded video exists.
        let uploadedMedia: LessonMediaResult | null = null;
        if (mode === "lesson" && lessonMediaFile && !pendingVideo) {
          uploadedMedia = await uploadLessonMediaFile(
            lessonMediaFile,
            studio.id,
          );
          if (!uploadedMedia) {
            toast.error("Failed to upload lesson media");
            return;
          }
        }

        // Video always resolves to a Mux-backed Video row now — from the
        // recorder (pendingVideoId), the library picker (also
        // pendingVideoId), or a just-uploaded file (uploadedMedia.videoId).
        const effectiveVideoId =
          pendingVideoId ??
          (uploadedMedia?.type === "video" ? uploadedMedia.videoId : null);

        let result: any;

        if (isEditMode && editPost) {
          // ---- Edit mode: update existing post ----
          // Raw videoUrl/videoDuration only matter for a pre-migration post
          // being edited without a new video attached — any new upload
          // (recorder, file browse, or library picker) resolves via
          // effectiveVideoId instead.
          const resolvedVideoUrl = effectiveVideoId
            ? null
            : (pendingVideo?.url ?? null);
          const resolvedVideoDuration = effectiveVideoId
            ? null
            : (pendingVideo?.duration ?? null);
          const resolvedAudioUrl =
            uploadedMedia?.type === "audio"
              ? uploadedMedia.url
              : (pendingAudio?.url ?? null);
          const resolvedAudioDuration =
            uploadedMedia?.type === "audio"
              ? uploadedMedia.duration
              : (pendingAudio?.duration ?? null);
          const resolvedAudioMimeType =
            uploadedMedia?.type === "audio"
              ? uploadedMedia.mimeType
              : (pendingAudio?.mimeType ?? null);

          // Narrowed from the legacy full-FullPost payload to the fields this
          // modal actually edits — absent fields keep their server values.
          try {
            result = await updatePostMutation.mutateAsync({
              id: editPost.id,
              data: {
                title: title.trim(),
                content: editorContent.trim() || null,
                channelId: selectedChannelId,
                image: imageUrl ?? coverImage ?? editPost.image,
                published:
                  options.mode === "publish"
                    ? true
                    : options.mode === "draft"
                      ? false
                      : (editPost.published ?? undefined),
                scheduledAt:
                  options.mode === "schedule"
                    ? options.scheduledAt
                    : editPost.scheduledAt,
                audioUrl: resolvedAudioUrl,
                audioDuration: resolvedAudioDuration,
                audioMimeType: resolvedAudioMimeType,
                // pendingVideoId is seeded from editPost.videoId when the
                // modal opens, so null here means the user explicitly removed
                // the video — don't fall back to the stale server value.
                videoId: effectiveVideoId,
                videoUrl: effectiveVideoId ? null : resolvedVideoUrl,
                videoDuration: effectiveVideoId ? null : resolvedVideoDuration,
                ...(effectiveVideoId
                  ? { type: "VIDEO" as const }
                  : editPost.videoId && editPost.type === "VIDEO"
                    ? { type: "TEXT" as const }
                    : {}),
              },
            });

            // Sync tags
            await setTagsMutation.mutateAsync({
              postId: editPost.id,
              tagIds: tags.map((tag) => tag.id),
            });
          } catch (error) {
            toast.error(
              error instanceof Error ? error.message : "Failed to update post",
            );
            return;
          }

          toast.success("Post updated");
        } else {
          // ---- Create mode: new post ----
          const audioFields =
            uploadedMedia?.type === "audio"
              ? {
                  audioUrl: uploadedMedia.url,
                  audioDuration: uploadedMedia.duration,
                  audioMimeType: uploadedMedia.mimeType,
                }
              : pendingAudio
                ? {
                    audioUrl: pendingAudio.url,
                    audioDuration: pendingAudio.duration,
                    audioMimeType: pendingAudio.mimeType,
                  }
                : {};
          const videoFields = effectiveVideoId
            ? { videoId: effectiveVideoId }
            : {};

          try {
            result = await createPostMutation.mutateAsync({
              title: title.trim(),
              channelId: selectedChannelId,
              ...(defaultCourseSectionId
                ? { courseSectionId: defaultCourseSectionId }
                : {}),
              ...(editorContent.trim() ? { content: editorContent } : {}),
              ...(options.mode === "publish" ? { published: true } : {}),
              ...(options.mode === "schedule"
                ? { scheduledAt: options.scheduledAt }
                : {}),
              ...(imageUrl ? { image: imageUrl } : {}),
              ...audioFields,
              ...videoFields,
              ...(canPostAsStudio && postAsStudio
                ? { postAsStudio: true }
                : {}),
            });

            // Assign tags to the created post
            if (tags.length > 0 && result?.id) {
              await setTagsMutation.mutateAsync({
                postId: result.id,
                tagIds: tags.map((tag) => tag.id),
              });
            }
          } catch (error) {
            toast.error(
              error instanceof Error ? error.message : "Failed to create post",
            );
            return;
          }

          va.track(ANALYTICS_EVENT);

          if (options.mode === "schedule") {
            toast.success(
              `Post scheduled for ${options.scheduledAt.toLocaleString(
                undefined,
                {
                  dateStyle: "medium",
                  timeStyle: "short",
                },
              )}`,
            );
          } else if (options.mode === "draft") {
            toast.success("Draft saved");
          }
        }

        onSaved?.();

        resetForm();
        onOpenChange(false);

        if (isEditMode) {
          router.refresh();
        } else if (options.mode === "publish" && selectedChannelId) {
          window.dispatchEvent(
            new CustomEvent("post-created", {
              detail: { channelId: selectedChannelId },
            }),
          );
        } else {
          router.refresh();
        }
      });
    },
    [
      studio?.id,
      coverFile,
      coverImage,
      title,
      selectedChannelId,
      editorContent,
      tags,
      isPending,
      isEditMode,
      editPost,
      pendingAudio,
      pendingVideo,
      resetForm,
      onOpenChange,
      router,
      defaultCourseSectionId,
      onSaved,
      isTagPending,
      pendingVideoId,
      createPostMutation,
      updatePostMutation,
      setTagsMutation,
      canPostAsStudio,
      postAsStudio,
    ],
  );

  const handlePublish = useCallback(() => {
    if (isSubmitDisabled) return;
    submitPost({ mode: "publish" });
  }, [isSubmitDisabled, submitPost]);

  const handleSchedule = useCallback(
    (date: Date) => {
      setShowScheduleModal(false);
      submitPost({ mode: "schedule", scheduledAt: date });
    },
    [submitPost],
  );

  const handleSaveDraft = useCallback(() => {
    if (isSubmitDisabled) return;
    submitPost({ mode: "draft" });
  }, [isSubmitDisabled, submitPost]);

  const handlePublishMenuSelect = useCallback(
    (action: PublishActionId) => {
      if (action === "schedule") {
        setShowScheduleModal(true);
      } else if (action === "draft") {
        handleSaveDraft();
      }
    },
    [handleSaveDraft],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      // Don't let the post modal close while the schedule picker is open —
      // closing it would reset the form and destroy the user's draft.
      if (!nextOpen && showScheduleModal) return;
      if (!nextOpen) {
        resetForm();
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange, resetForm, showScheduleModal],
  );

  // ---- Render ----

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          hideCloseButton
          aria-describedby={undefined}
          onInteractOutside={(e) => e.preventDefault()}
          className={`flex flex-col gap-0 overflow-hidden p-0 ${
            isExpanded
              ? "!left-0 !top-0 !h-screen !max-h-none !w-[33vw] !min-w-[420px] !max-w-none !translate-x-0 !translate-y-0 !rounded-none !border-0 !border-r !shadow-xl md:!w-[33vw] md:!max-w-none"
              : "!h-[75vh] !max-h-[750px] !w-[70vw] !max-w-[1000px] md:!w-[70vw] md:!max-w-[1000px]"
          }`}
        >
          <DialogTitle className="sr-only">
            {isEditMode ? copy.editTitle : copy.createTitle}
          </DialogTitle>
          {/* ---- Header ---- */}
          <div className="border-grunge15 dark:border-grunge85 flex items-center justify-between border-b px-6 py-4">
            <h2
              id="create-post-title"
              className="text-lg font-semibold text-grunge dark:text-white"
            >
              {isEditMode ? copy.editTitle : copy.createTitle}
            </h2>
            <div className="flex items-center gap-1">
              {/* Add Cover */}
              {!coverImage && (
                <button
                  type="button"
                  onClick={() => coverInputRef.current?.click()}
                  className="flex items-center gap-1 rounded px-2 py-1 text-sm text-grunge40 transition-colors hover:text-grunge60 dark:text-grunge60 dark:hover:text-grunge40"
                >
                  <ImageIcon className="h-4 w-4" />
                  <span>Add Cover</span>
                </button>
              )}
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                onChange={handleCoverSelect}
                className="hidden"
              />

              {/* Expand toggle (dock to side) */}
              <button
                type="button"
                onClick={toggleExpanded}
                aria-label={isExpanded ? "Collapse to modal" : "Expand to side"}
                className={ICON_CLASSES}
              >
                {isExpanded ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </button>

              {/* Close */}
              <button
                type="button"
                onClick={() => handleOpenChange(false)}
                aria-label="Close"
                className={ICON_CLASSES}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* ---- Body ----
            min-h-0 lets the flex item shrink below its content (default in
            flex children is min-height: auto, which would let the editor's
            hardcoded 70vh push the modal past its height cap). The
            wrapperClassName="!h-full" prop on SimpleEditor overrides the
            70vh inside this modal only — without touching the shared SCSS
            rule that admin full-page editors still rely on. */}
          <div
            className={`flex min-h-0 flex-1 flex-col px-6 py-4 ${isVideoMode && !pendingVideo ? "overflow-hidden" : "overflow-y-auto"}`}
          >
            {/* ---- Video recorder (replaces editor when active) ---- */}
            {isVideoMode && !pendingVideo && (
              <VideoMessageRecorder
                studioId={studio?.id ?? ""}
                onSend={async (videoId, duration) => {
                  // pendingVideo is kept as a boolean "video attached" flag —
                  // the recorder now uploads through Mux, so the real
                  // reference is pendingVideoId; this dummy url only drives
                  // the existing show/hide gating below.
                  setPendingVideo({ url: "", duration });
                  setPendingVideoId(videoId);
                  setIsVideoMode(false);
                }}
                onCancel={() => {
                  setIsVideoMode(false);
                }}
              />
            )}

            {/* Normal content — hidden while the video recorder is active */}
            {(!isVideoMode || pendingVideo) && coverImage && (
              <div className="relative mb-4">
                <img
                  src={coverImage}
                  alt="Cover preview"
                  className="h-48 w-full rounded-lg object-cover"
                />
                <button
                  type="button"
                  onClick={removeCover}
                  className="absolute right-2 top-2 rounded-full bg-grunge/70 p-1 text-white transition-colors hover:bg-grunge dark:bg-white/20 dark:hover:bg-white/40"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Title + editor — hidden while the video recorder is active */}
            {(!isVideoMode || pendingVideo) && (
              <>
                <input
                  type="text"
                  placeholder={copy.titlePlaceholder}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={MAX_TITLE_LENGTH}
                  aria-label={copy.titlePlaceholder}
                  aria-invalid={isTitleTooLong}
                  aria-describedby="create-post-title-hint"
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text/plain");
                    if (pasted) {
                      e.preventDefault();
                      const clean = pasted
                        .replace(/^#{1,6}\s+/gm, "")
                        .replace(/\*\*(.+?)\*\*/g, "$1")
                        .replace(/\*(.+?)\*/g, "$1")
                        .replace(/__(.+?)__/g, "$1")
                        .replace(/_(.+?)_/g, "$1")
                        .replace(/~~(.+?)~~/g, "$1")
                        .replace(/`(.+?)`/g, "$1")
                        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
                        .replace(/!\[[^\]]*\]\([^)]+\)/g, "")
                        .replace(/^>\s+/gm, "")
                        .replace(/^[-*+]\s+/gm, "")
                        .replace(/^\d+\.\s+/gm, "")
                        .replace(/\n+/g, " ")
                        .trim();
                      const input = e.currentTarget;
                      const start = input.selectionStart ?? 0;
                      const end = input.selectionEnd ?? 0;
                      const room =
                        MAX_TITLE_LENGTH - (title.length - (end - start));
                      const inserted = clean.slice(0, Math.max(room, 0));
                      const next =
                        title.slice(0, start) + inserted + title.slice(end);
                      setTitle(next);
                      requestAnimationFrame(() => {
                        input.setSelectionRange(
                          start + inserted.length,
                          start + inserted.length,
                        );
                      });
                    }
                  }}
                  className="w-full border-0 bg-transparent p-0 text-2xl font-bold text-grunge placeholder:text-grunge30 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-grunge60"
                />
                {title.length >= MAX_TITLE_LENGTH - 20 && (
                  <p
                    id="create-post-title-hint"
                    role={isTitleTooLong ? "alert" : undefined}
                    className={`mt-1 text-xs ${isTitleTooLong ? "text-destructive" : "text-muted-foreground"}`}
                  >
                    {isTitleTooLong
                      ? `Title must be ${MAX_TITLE_LENGTH} characters or fewer`
                      : `${title.length}/${MAX_TITLE_LENGTH}`}
                  </p>
                )}
              </>
            )}

            {(!isVideoMode || pendingVideo) && mode === "lesson" && (
              <div className="mt-4">
                <LessonMediaUploadCard
                  file={lessonMediaFile}
                  onFile={(f) => {
                    setLessonMediaFile(f);
                    if (f) {
                      setPendingVideoId(null);
                      setPendingVideoTitle(null);
                    }
                  }}
                  onRecord={() => {
                    setPendingVideoId(null);
                    setPendingVideoTitle(null);
                    setIsVideoMode(true);
                  }}
                  onSelectExisting={() => setShowVideoLibrary(true)}
                  selectedVideo={
                    pendingVideoId
                      ? {
                          id: pendingVideoId,
                          title: pendingVideoTitle ?? "Video from library",
                        }
                      : null
                  }
                  onClearSelected={() => {
                    setPendingVideoId(null);
                    setPendingVideoTitle(null);
                  }}
                  hasAttachedVideo={pendingVideo !== null}
                  attachedVideoDuration={pendingVideo?.duration ?? null}
                  onClearAttached={() => {
                    setPendingVideo(null);
                    setPendingVideoId(null);
                  }}
                />
              </div>
            )}

            {/* TRA-1551: AI draft for library (Mux-backed) videos — keyed by
                video id so dismissal/inserted state resets when the video
                changes. Direct-R2 uploads have no transcript → no card. */}
            {(!isVideoMode || pendingVideo) && pendingVideoId && (
              <AiDescriptionCard
                key={pendingVideoId}
                videoId={pendingVideoId}
                onInsert={handleInsertAiDraft}
              />
            )}

            {/* Kept mounted (hidden, not unmounted) while the video recorder
                is active — SimpleEditor owns its tiptap document internally,
                so removing it from the tree would destroy the typed body
                (TRA-2105). Only the wrapper's visibility toggles. */}
            <div
              className={`mt-4 flex min-h-0 flex-1 flex-col [&_.tiptap.ProseMirror.simple-editor]:!p-0 ${
                isVideoMode && !pendingVideo ? "hidden" : ""
              }`}
            >
              <SimpleEditor
                onReady={setEditor}
                onUpdate={handleEditorUpdate}
                wrapperClassName="!h-full !max-h-full !min-h-0"
                hideToolbar
                showSelectionToolbar
                placeholder={copy.bodyPlaceholder}
                mentionSuggestion={mentionSuggestion}
              />
            </div>

            {/* Topics section */}
            <div className="border-grunge15 dark:border-grunge85 mt-4 border-t pt-4">
              <div className="flex flex-wrap items-center gap-2">
                {/* Selected tag pills */}
                {tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="flex items-center gap-1 rounded-full bg-grunge10 px-2.5 py-0.5 text-xs dark:bg-grunge80"
                  >
                    <span className="text-grunge dark:text-white">
                      {tag.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag.id)}
                      className="text-grunge40 transition-colors hover:text-grunge60 dark:text-grunge60 dark:hover:text-grunge40"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}

                {/* Tag input */}
                {tags.length < MAX_TOPICS && (
                  <div className="relative">
                    <input
                      ref={tagInputRef}
                      type="text"
                      placeholder={
                        tags.length === 0
                          ? "Choose up to 5 topics"
                          : "Add topic..."
                      }
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onFocus={() => setIsTagInputFocused(true)}
                      onBlur={() => setIsTagInputFocused(false)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.stopPropagation();
                          e.nativeEvent.stopImmediatePropagation();
                          e.preventDefault();
                          if (tagInput.trim()) {
                            const match = filteredTags.find(
                              (t) =>
                                t.name.toLowerCase() === tagInput.toLowerCase(),
                            );
                            if (match) {
                              handleAddTag(match);
                            } else {
                              handleCreateTag();
                            }
                          }
                        }
                      }}
                      className="w-40 border-0 bg-transparent p-0 text-sm text-grunge placeholder:text-grunge30 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-grunge60"
                    />

                    {/* Tag dropdown — opens upward so it doesn't overlap the
                      post body text below the topics row */}
                    {showTagDropdown && (
                      <div className="absolute bottom-full left-0 z-50 mb-1 max-h-48 w-56 overflow-y-auto rounded-lg border border-border bg-popover shadow-lg">
                        {filteredTags.map((tag) => (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() => handleAddTag(tag)}
                            className="flex w-full items-center px-3 py-2 text-left text-sm text-popover-foreground transition-colors hover:bg-accent"
                          >
                            {tag.name}
                          </button>
                        ))}
                        {!hasExactMatch(allTags, tagInput) &&
                          tagInput.trim() && (
                            <button
                              type="button"
                              onClick={handleCreateTag}
                              className="flex w-full items-center gap-1 px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-accent"
                            >
                              <Plus className="h-3 w-3" />
                              <span>
                                Create topic &ldquo;{tagInput.trim()}&rdquo;
                              </span>
                            </button>
                          )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ---- Audio recorder / preview ---- */}
          {isAudioMode && !pendingAudio && (
            <div className="border-grunge15 dark:border-grunge85 border-t px-6 py-3">
              <VoiceMessageRecorder
                studioId={studio?.id ?? ""}
                onSend={async (url, duration, mimeType) => {
                  setPendingAudio({ url, duration, mimeType });
                  setIsAudioMode(false);
                }}
                onCancel={() => setIsAudioMode(false)}
              />
            </div>
          )}
          {pendingAudio && (
            <div className="border-grunge15 dark:border-grunge85 flex items-center gap-2 border-t px-6 py-2">
              <Mic className="h-4 w-4 flex-shrink-0 text-primary" />
              <span className="flex-1 text-sm text-foreground">
                Voice message attached
              </span>
              <button
                type="button"
                onClick={() => setPendingAudio(null)}
                className="rounded p-1 text-muted-foreground hover:text-foreground"
                aria-label="Remove voice message"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* ---- Video recorder / preview ----
              In lesson mode the attached video renders inside the media
              upload card instead, so the chip is redundant there. */}
          {pendingVideo && mode !== "lesson" && (
            <div className="border-grunge15 dark:border-grunge85 flex items-center gap-2 border-t px-6 py-2">
              <Video className="h-4 w-4 flex-shrink-0 text-primary" />
              <span className="flex-1 text-sm text-foreground">
                Video message attached
              </span>
              <button
                type="button"
                onClick={() => {
                  setPendingVideo(null);
                  setPendingVideoId(null);
                }}
                className="rounded p-1 text-muted-foreground hover:text-foreground"
                aria-label="Remove video message"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* ---- Footer ---- */}
          <div className="border-grunge15 dark:border-grunge85 flex items-center justify-between border-t px-6 py-3">
            {/* Left toolbar icons */}
            <div className="flex items-center gap-0.5">
              <BlockInsertMenu onSelect={handleBlockSelect}>
                <button
                  type="button"
                  aria-label="Insert block"
                  className={ICON_CLASSES}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </BlockInsertMenu>
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                aria-label="Attach file"
                className={ICON_CLASSES}
              >
                <Paperclip className="h-4 w-4" />
              </button>
              <EmojiPickerButton
                onSelect={handleEmojiInsert}
                className={ICON_CLASSES}
              />
              <button
                type="button"
                onClick={() => setIsVideoMode((v) => !v)}
                aria-label="Record video message"
                className={`${ICON_CLASSES} ${pendingVideo ? "text-primary" : ""}`}
              >
                <Video className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setIsAudioMode((v) => !v)}
                aria-label="Record voice message"
                className={`${ICON_CLASSES} ${pendingAudio ? "text-primary" : ""}`}
              >
                <Mic className="h-4 w-4" />
              </button>
            </div>

            {/* Right: Channel selector + Publish */}
            <div className="flex items-center gap-3">
              {/* Author selector (TRA-1443) — owners/admins choose between
                  posting as the Studio brand (default) or themselves. */}
              {canPostAsStudio && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowAuthorDropdown((prev) => !prev)}
                    className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <span>Posting as:</span>
                    <span className="font-medium text-foreground">
                      {postAsStudio
                        ? (studio?.name ?? "Studio")
                        : (currentMember?.profile?.displayName ?? "Myself")}
                    </span>
                    <ChevronDown className="h-3 w-3" />
                  </button>

                  {showAuthorDropdown && (
                    <div className="absolute bottom-full right-0 z-50 mb-1 w-56 overflow-y-auto rounded-lg border border-border bg-popover text-popover-foreground shadow-lg">
                      <button
                        type="button"
                        onClick={() => {
                          setPostAsStudio(true);
                          setShowAuthorDropdown(false);
                        }}
                        className={`flex w-full items-center px-3 py-2 text-left text-sm transition-colors hover:bg-accent ${
                          postAsStudio
                            ? "font-medium text-foreground"
                            : "text-muted-foreground"
                        }`}
                      >
                        {studio?.name ?? "Studio"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPostAsStudio(false);
                          setShowAuthorDropdown(false);
                        }}
                        className={`flex w-full items-center px-3 py-2 text-left text-sm transition-colors hover:bg-accent ${
                          !postAsStudio
                            ? "font-medium text-foreground"
                            : "text-muted-foreground"
                        }`}
                      >
                        {currentMember?.profile?.displayName ?? "Myself"}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Channel selector — hidden in modes where the target channel
                  is implied by context (e.g. lessons). */}
              {copy.showChannelSelector && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowChannelDropdown((prev) => !prev)}
                    className="flex items-center gap-1 text-sm text-grunge40 transition-colors hover:text-grunge60 dark:text-grunge60 dark:hover:text-grunge40"
                  >
                    <span>Posting in:</span>
                    <span className="font-medium text-grunge dark:text-white">
                      {selectedChannel
                        ? `${selectedChannel.emoji ? `${selectedChannel.emoji} ` : ""}${selectedChannel.name}`
                        : "Select channel"}
                    </span>
                    <ChevronDown className="h-3 w-3" />
                  </button>

                  {showChannelDropdown && (
                    <div className="border-grunge15 dark:border-grunge85 absolute bottom-full right-0 z-50 mb-1 max-h-48 w-56 overflow-y-auto rounded-lg border bg-white shadow-lg dark:bg-grunge">
                      {channels.map((ch) => (
                        <button
                          key={ch.id}
                          type="button"
                          onClick={() => handleSelectChannel(ch.id)}
                          className={`flex w-full items-center px-3 py-2 text-left text-sm transition-colors hover:bg-grunge10 dark:hover:bg-grunge80 ${
                            ch.id === selectedChannelId
                              ? "font-medium text-grunge dark:text-white"
                              : "text-grunge60 dark:text-grunge40"
                          }`}
                        >
                          {ch.emoji ? `${ch.emoji} ${ch.name}` : ch.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Publish split button — left half publishes now, right half
                opens the PublishMenu (Schedule / Save as draft). */}
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={isSubmitDisabled}
                  className="inline-flex h-8 items-center justify-center rounded-l-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isPending ? (
                    <LoadingDots />
                  ) : isEditMode ? (
                    copy.submitEdit
                  ) : (
                    copy.submitCreate
                  )}
                </button>
                <PublishMenu
                  onSelect={handlePublishMenuSelect}
                  disabled={isSubmitDisabled}
                >
                  <button
                    type="button"
                    disabled={isSubmitDisabled}
                    aria-label="More publish options"
                    className="inline-flex h-8 items-center justify-center rounded-r-lg border-l border-primary-foreground/20 bg-primary px-2 text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </PublishMenu>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule picker — rendered OUTSIDE the CreatePostModal Dialog so it
        doesn't inherit the parent Dialog's transform (which would break
        its own fixed-position overlay). Controlled-open via state. */}
      <SchedulePickerModal
        open={showScheduleModal}
        onOpenChange={setShowScheduleModal}
        onConfirm={handleSchedule}
      />

      {/* Video library picker — a nested Radix Dialog so focus, scroll-lock and
          pointer events are handled by Radix's modal layer stack (it pauses the
          parent dialog's focus trap). The DialogContent surface is neutralised
          and its close button hidden so SelectVideoModal's own card + X are the
          only visible chrome (no doubled modal). */}
      <Dialog open={showVideoLibrary} onOpenChange={setShowVideoLibrary}>
        <DialogContent
          hideCloseButton
          aria-describedby={undefined}
          className="!w-auto !max-w-md border-0 bg-transparent !p-0 shadow-none"
        >
          <DialogTitle className="sr-only">Video library</DialogTitle>
          <SelectVideoModal
            isLoading={isImportingVideo}
            onClose={() => setShowVideoLibrary(false)}
            onConfirmClick={(video) => {
              setIsImportingVideo(true);
              try {
                setPendingVideoTitle(getDisplayTitle(video));
                setPendingVideoId(video.id);
                setLessonMediaFile(null);
                setPendingVideo(null);
                setShowVideoLibrary(false);
              } finally {
                setIsImportingVideo(false);
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
